/**
 * taskLineTransformer.js
 *
 * Responsible for mutating or injecting new tokens (priority, tags,
 * attributes) back into a task line.
 *
 * Follows CLEAN principles by separating transformation from
 * extraction/parsing logic.
 */

import { validatePriority } from './taskLineHelpers';
import {
  extractPriority,
  extractTags,
  extractAttribute
} from './taskLineExtractor';

/**
 * Injects the given priorityChar into a line, removing any
 * existing "!X" before adding the new one.
 *
 * @param {string} line - The line to transform.
 * @param {string} priority - The single-character priority to insert.
 * @returns {string} - The updated line with the new priority.
 */
export const injectPriority = (line, priority) => {
  const safeLine = line || '';

  // If no priority char, strip existing "!X".
  if (!priority) {
    const { line: stripped } = extractPriority(safeLine);
    return stripped;
  }

  // Validate priority
  if (!validatePriority(priority)) {
    return safeLine;
  }

  const { line: stripped } = extractPriority(safeLine);
  return `!${priority} ${stripped}`.trim();
};

/**
 * Injects tagsString back into line, removing any existing "#foo"
 * occurrences first. Then appends new tags at the end of the line.
 *
 * @param {string} line - The original line.
 * @param {string} tagsString - Space-separated tags to inject.
 * @returns {string} - The line updated with the new tags.
 */
export const injectTagsIntoLine = (line, tagsString) => {
  const safeTags = tagsString || '';
  const { tags: newTags } = extractTags(safeTags);
  const { line: stripped } = extractTags(line || '');

  return (stripped + ' ' + newTags.join(' ')).trim();
};

/**
 * Serializes one attribute value using canonical output rules:
 * - numbers/booleans stay unquoted so downstream parsing keeps the type
 * - every other input is treated as string and emitted quoted
 *
 * @param {string|number|boolean|null|undefined} value
 * @returns {string}
 */
const serializeAttributeValue = (value) => {
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }

  const safeString = String(value ?? '');
  // Escape before wrapping in quotes so the resulting line stays parseable.
  const escaped = safeString.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
  return `"${escaped}"`;
};

/**
 * Injects or updates the specified attribute into the line.
 *
 * @param {string} currentLine - The current line string.
 * @param {string} key - The attribute key.
 * @param {string|number|boolean} value - The attribute value.
 * @returns {string} - The updated line with the new or updated attribute.
 */
export const injectAttribute = (currentLine, key, value) => {
  const { attribute, line: baseLine } = extractAttribute(currentLine, key);
  // Canonical write path: all branches use the same serializer so updates and
  // inserts produce consistent output for strings vs number/boolean values.
  const serializedValue = serializeAttributeValue(value);
  const attrInsertion = `${key}: ${serializedValue}`;

  let updatedLine;
  if (attribute) {
    // Replace existing attribute value
    updatedLine = currentLine.replace(
      new RegExp(`(\\b${key}\\s*:\\s*)([^,}]+)`, 'i'),
      `$1${serializedValue} `
    );
  } else if (/\{[^}]*\}$/.test(baseLine)) {
    // Append new attribute
    updatedLine = baseLine.replace(
      /\{([^}]*)\}$/,
      (_, existing) => `{ ${existing.trim()}, ${attrInsertion} }`
    );
  } else {
    // Create new attributes block if none
    updatedLine = `${baseLine} { ${attrInsertion} }`;
  }

  // Remove empty blocks that may appear due to trailing commas
  updatedLine = updatedLine.replace(/\{\s*,\s*\}/, '{}').trim();

  return updatedLine;
};
